import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addCampaignRecipients,
  fetchBudgetAmendments,
  fetchCampaignContacts,
  quoteRecipientAdditions,
  removeCampaignContact,
  resendCampaignContactEmail,
  updateCampaignContact,
} from "@/api/campaignContacts";
import { useCampaignEndpoints } from "@/context/CampaignEndpointsContext";
import type {
  AddCampaignRecipientsBody,
  QuoteCampaignRecipientsBody,
  UpdateCampaignContactBody,
} from "@/interfaces/campaignContacts";

/** Credit caches an added recipient batch can move (it may buy credits or draw on wallets). */
export const CREDIT_QUERY_KEYS = [
  ["creditSummary"],
  ["creditLots"],
  ["groups"],
  ["orgCreditBalance"],
  ["clientPurchases"],
] as const;

/**
 * GFG-235 — mirrors `useBouncedEmails`'s shape: same resource kind (one campaign's contacts), same
 * modal-on-demand usage. `enabled` lets the caller (`ManageContactsModal`) fetch only while the
 * modal is open, instead of on every render of the card behind it.
 */
export function useCampaignContacts(campaignId: string, enabled = true) {
  const path = useCampaignEndpoints().campaign(campaignId);
  return useQuery({
    queryKey: ["campaignContacts", campaignId, path],
    queryFn: () => fetchCampaignContacts(campaignId, path),
    enabled: enabled && !!campaignId,
  });
}

/**
 * Price of adding `body.count` recipients with the chosen postpaid `depositMode` (absent means the
 * server default, card). Keyed under `["campaignContacts", id]`, so invalidating the contacts list
 * after a 422 (quote changed, credits moved) re-quotes automatically. Never kept past its last
 * observer (`gcTime: 0`), so reopening the wizard always asks the server. While a new deposit mode
 * loads, the previous quote stays on screen (`placeholderData`); callers must check
 * `isPlaceholderData` / `isFetching` and not act on that stale price.
 */
export function useRecipientAdditionsQuote(
  campaignId: string,
  body: QuoteCampaignRecipientsBody,
  enabled: boolean,
) {
  const path = useCampaignEndpoints().campaign(campaignId);
  return useQuery({
    queryKey: [
      "campaignContacts",
      campaignId,
      "additionsQuote",
      path,
      body.count,
      body.depositMode ?? "card",
    ],
    queryFn: () => quoteRecipientAdditions(campaignId, body, path),
    enabled: enabled && !!campaignId && body.count > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/** The campaign's budget amendment history, oldest first. */
export function useBudgetAmendments(campaignId: string, enabled: boolean) {
  const path = useCampaignEndpoints().campaign(campaignId);
  return useQuery({
    queryKey: ["campaignBudgetAmendments", campaignId, path],
    queryFn: () => fetchBudgetAmendments(campaignId, path),
    enabled: enabled && !!campaignId,
  });
}

/**
 * The contacts modal's per-recipient actions. No optimistic update: invalidate once settled (a 409
 * means the list is stale too), the list, `["campaigns"]` (the card's recipient count and alerts)
 * and the budget history, since adding recipients can amend the budget. A removal frees a space and
 * never moves money.
 */
export function useCampaignContactActions(campaignId: string) {
  const queryClient = useQueryClient();
  const endpoints = useCampaignEndpoints();
  const path = endpoints.campaign(campaignId);

  function invalidate(keepQuote = false) {
    // A settled add spent the slots its quote priced, so refetching that quote would only 409.
    queryClient.invalidateQueries({
      queryKey: ["campaignContacts", campaignId],
      predicate: (query) => !keepQuote || query.queryKey[2] !== "additionsQuote",
    });
    queryClient.invalidateQueries({ queryKey: endpoints.listKey });
    queryClient.invalidateQueries({ queryKey: ["campaignBudgetAmendments", campaignId] });
  }

  const update = useMutation({
    mutationFn: ({ contactId, body }: { contactId: number; body: UpdateCampaignContactBody }) =>
      updateCampaignContact(campaignId, contactId, body, path),
    onSettled: () => invalidate(),
  });

  const resend = useMutation({
    mutationFn: (contactId: number) => resendCampaignContactEmail(campaignId, contactId, path),
    onSettled: () => invalidate(),
  });

  const remove = useMutation({
    mutationFn: (contactId: number) => removeCampaignContact(campaignId, contactId, path),
    onSettled: () => invalidate(),
  });

  const addRecipients = useMutation({
    mutationFn: (body: AddCampaignRecipientsBody) => addCampaignRecipients(campaignId, body, path),
    onSettled: (_result, error) => {
      invalidate(!error);
      for (const queryKey of CREDIT_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  return { update, resend, remove, addRecipients };
}
